import { AfterViewInit, Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DataFormatterService } from '../../services/data-formatter.service';
import * as _ from 'lodash-es';

export interface RawDataDialogData {
  title: string;
  data: any[];
  component_id: string;
}

@Component({
    selector: 'gtw-raw-data-dialog',
    templateUrl: './raw-data-dialog.component.html',
    styleUrls: ['./raw-data-dialog.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RawDataDialogComponent implements OnInit, AfterViewInit {

  columns: { field: string; header: string }[] = [];
  displayedColumns: string[] = [];
  filterFields: string[] = [];
  dataSource = new MatTableDataSource<any>([]);
  minimise: boolean = false;
  globalFilter: string = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RawDataDialogData,
    private dialogRef: MatDialogRef<RawDataDialogComponent>,
    private dataFormatterService: DataFormatterService
  ) {}

  ngOnInit(): void {
    if (this.data.data && this.data.data.length > 0) {
      this.columns = Object.keys(this.data.data[0]).map(key => ({
        field: key,
        header: key
      }));
      this.displayedColumns = this.columns.map(c => c.field);
      this.filterFields = this.columns.map(c => c.field);
      this.dataSource.data = [...this.data.data];
      this.dataSource.filterPredicate = (row, filter) => this.rowMatchesFilter(row, filter);
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /**
   * Legacy parity: gtViewRawDataCtrl (dashboardExecutiveCtrl.js) uses DataTables
   * column render to format numbers, then DataTables global search matches the
   * rendered cell text (display output), not the raw numeric source value.
   */
  private getLegacyDisplayValue(value: any): string {
    if (_.isNumber(value)) {
      const formatter = this.dataFormatterService.getFormatter('Number');
      if (formatter) {
        return formatter.func(value, false, false);
      }
    }
    return value != null ? String(value) : '';
  }

  /** Searchable cell text — legacy rendered output with HTML stripped. */
  private getCellSearchText(value: any): string {
    return this.getLegacyDisplayValue(value).replace(/<[^>]*>/g, '');
  }

  private rowMatchesFilter(row: any, filter: string): boolean {
    const term = filter.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return this.filterFields.some(field =>
      this.getCellSearchText(row[field]).toLowerCase().includes(term)
    );
  }

  formatValue(value: any): string {
    if (_.isNumber(value)) {
      const formatter = this.dataFormatterService.getFormatter('Number');
      if (formatter) {
        return formatter.func(value, false, true);
      }
    }
    return value != null ? String(value) : '-';
  }

  applyGlobalFilter(value: string): void {
    this.dataSource.filter = (value || '').trim().toLowerCase();
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  clearGlobalFilter(): void {
    this.globalFilter = '';
    this.applyGlobalFilter('');
  }

  close(): void {
    this.dialogRef.close();
  }
}
